<script setup lang="ts">
import { cityMetaDescription, citySummary, pluralCz } from '~/composables/useSeoHelpers'

const route = useRoute()
const router = useRouter()

const countySlug = computed(() => route.params.county as string)
const citySlug = computed(() => route.params.city as string)

const page = computed(() => Number(route.query.page) || 1)
const limit = 20

const offset = computed(() => (page.value - 1) * limit)

const { data: county } = await useCounty(countySlug)
const { data: city, pending: cityPending } = await useCity(countySlug, citySlug)

const { activeFilter, setFilter } = useClinicFilter()

// Derive server query params from activeFilter
const clinicsOptions = computed(() => ({
  citySlug: citySlug.value,
  isEmergency: activeFilter.value === 'emergency' || undefined,
  is24Hours: activeFilter.value === 'nonstop' || undefined,
  openNow: activeFilter.value === 'open' || undefined,
  limit,
  offset: offset.value,
}))

const { data: clinicsData, pending: clinicsPending } = await useClinics(clinicsOptions)

const clinics = computed(() => clinicsData.value?.data || [])
const total = computed(() => clinicsData.value?.total || 0)
const totalPages = computed(() => Math.ceil(total.value / limit))

// City-wide counts from API (not from paginated clinics)
const emergencyCount = computed(() => city.value?.emergencyCount ?? 0)
const nonstopCount = computed(() => city.value?.nonstopCount ?? 0)

if (!cityPending.value && !city.value) {
  throw createError({
    statusCode: 404,
    message: 'Město nenalezeno',
  })
}

function onPageChange(newPage: number) {
  router.push({
    query: {
      ...route.query,
      page: newPage > 1 ? newPage : undefined,
    },
  })
}

// Empty state messages
const emptyStateTitle = computed(() => {
  if (activeFilter.value === 'open') return 'Momentálně není otevřena žádná klinika'
  if (activeFilter.value === 'emergency') return 'Žádná pohotovostní klinika'
  if (activeFilter.value === 'nonstop') return 'Žádná nonstop klinika'
  return 'Žádné kliniky'
})

const emptyStateDescription = computed(() => {
  if (activeFilter.value === 'open') return 'Momentálně není otevřena žádná klinika v tomto městě. Zkuste zobrazit všechny kliniky nebo vyhledejte v celém kraji.'
  if (activeFilter.value !== 'all') return 'Zkuste zobrazit všechny kliniky nebo vyhledejte v celém kraji.'
  return 'V tomto městě zatím nemáme žádné kliniky.'
})

useSeoMeta({
  title: () =>
    city.value
      ? emergencyCount.value > 0
        ? `Veterina ${city.value.name} – Veterinární kliniky a pohotovosti`
        : `Veterina ${city.value.name} – Veterinární kliniky`
      : 'Veterináři',
  description: () =>
    city.value && county.value
      ? cityMetaDescription(city.value.name, county.value.name, total.value, emergencyCount.value)
      : '',
  twitterCard: 'summary_large_image',
})

defineOgImage({
  props: {
    title: computed(() => city.value ? `Veterináři v ${city.value.name}` : ''),
    description: computed(() => city.value ? `${total.value} ${pluralCz(total.value, 'klinika', 'kliniky', 'klinik')}` : ''),
  },
})

useSchemaOrg([
  defineWebPage({
    '@type': 'CollectionPage',
  }),
])

const breadcrumbs = computed(() => [
  { label: 'Domů', to: '/' },
  { label: county.value?.name || '...', to: `/${countySlug.value}` },
  { label: city.value?.name || '...' },
])
</script>

<template>
  <div class="container mx-auto px-4 py-8">
    <UBreadcrumb :items="breadcrumbs" class="mb-6" />

    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl md:text-3xl font-bold text-highlighted">
        Veterináři v {{ city?.name || '...' }}
      </h1>
      <p class="text-muted mt-1">
        {{ total }} {{ pluralCz(total, 'klinika', 'kliniky', 'klinik') }}
        <button
          v-if="emergencyCount > 0"
          class="inline-flex items-center gap-1 ml-2 text-red-600 hover:underline cursor-pointer"
          @click="setFilter('emergency')"
        >
          <UIcon name="i-lucide-siren" class="w-3.5 h-3.5" />
          {{ emergencyCount }} {{ pluralCz(emergencyCount, 'pohotovost', 'pohotovosti', 'pohotovostí') }}
        </button>
        <button
          v-if="nonstopCount > 0"
          class="inline-flex items-center gap-1 ml-2 text-blue-600 hover:underline cursor-pointer"
          @click="setFilter('nonstop')"
        >
          <UIcon name="i-lucide-clock" class="w-3.5 h-3.5" />
          {{ nonstopCount }} nonstop
        </button>
      </p>
    </div>

    <!-- Filter bar -->
    <FilterBar
      class="mb-8"
      :radius-options="[
        { km: 10, to: { path: '/veteriny', query: { citySlug: citySlug, radiusKm: 10 } } },
        { km: 25, to: { path: '/veteriny', query: { citySlug: citySlug, radiusKm: 25 } } },
      ]"
    />

    <!-- Skeleton loading -->
    <div v-if="clinicsPending" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <USkeleton v-for="i in 6" :key="i" class="h-40 rounded-xl" />
    </div>

    <!-- Clinic grid -->
    <div v-else-if="clinics.length" class="space-y-8">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <ClinicCard
          v-for="clinic in clinics"
          :key="clinic.id"
          :clinic="clinic"
        />
      </div>

      <div v-if="totalPages > 1" class="flex justify-center">
        <UPagination
          :page="page"
          :total="total"
          :items-per-page="limit"
          @update:page="onPageChange"
        />
      </div>
    </div>

    <!-- Empty state -->
    <EmptyState
      v-else
      icon="i-lucide-search-x"
      :title="emptyStateTitle"
      :description="emptyStateDescription"
    >
      <div class="flex flex-wrap gap-3 justify-center">
        <UButton
          v-if="activeFilter !== 'all'"
          color="primary"
          @click="setFilter('all')"
        >
          Zobrazit všechny kliniky
        </UButton>
        <UButton
          v-if="activeFilter !== 'all'"
          color="neutral"
          variant="outline"
          :to="`/${countySlug}`"
        >
          Hledat v celém kraji
        </UButton>
      </div>
    </EmptyState>

    <!-- City description (SEO - moved to bottom) -->
    <p
      v-if="city && county && total > 0"
      class="mt-12 pt-8 border-t border-default text-base text-muted"
    >
      {{ citySummary(city.name, county.name, total, emergencyCount, nonstopCount) }}
    </p>
  </div>
</template>
